import React from 'react';
import type { RefObject } from 'react';
import type { IntlShape } from 'react-intl-next';

import type {
	PublicPluginAPI,
	QuickInsertHandlerFn,
	EditorCommand,
} from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import TemplateIcon from '@atlaskit/icon/core/app';
import type {
	QuickInsertItem,
	QuickInsertActionInsert,
} from '@atlaskit/editor-common/provider-factory';

import type { Snippet } from '../snippetTypes.ts';
import { createSnippetDescription, extractSnippetKeywords } from '../snippetUtils.ts';

export const addSnippetQuickInsertItem = ({
	snippet,
	editorAPIRef,
}: {
	snippet: Snippet;
	editorAPIRef: RefObject<PublicPluginAPI<[QuickInsertPlugin]> | null | undefined>;
}) => {
	if (!editorAPIRef?.current) {
		return;
	}
	addSnippetQuickInsertItemWithAPI({ snippet, editorAPI: editorAPIRef.current });
};

/**
 * Adds a snippet to the quick insert menu using the editor API directly.
 * Use this variant when you have the API object directly instead of a ref.
 */
export const addSnippetQuickInsertItemWithAPI = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: PublicPluginAPI<[QuickInsertPlugin]> | null | undefined;
}) => {
	if (!editorAPI) {
		return;
	}
	const addQuickInsertItem: ((item: QuickInsertHandlerFn) => EditorCommand) | undefined =
		editorAPI?.quickInsert?.commands?.addQuickInsertItem;

	if (addQuickInsertItem === undefined) {
		return;
	}

	editorAPI.core?.actions.execute(
		addQuickInsertItem((intl: IntlShape) => [createSnippetQuickInsertItem({ snippet, intl })]),
	);
};

const createSnippetQuickInsertItem = ({
	snippet,
	intl,
}: {
	snippet: Snippet;
	intl: IntlShape;
}): QuickInsertItem => {
	return {
		key: `snippet-${snippet.snippetId}`,
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl),
		keywords: extractSnippetKeywords(snippet.name, intl),
		isDisabledOffline: true,
		icon: () => <TemplateIcon label="Snippet" />,
		action: (insert: QuickInsertActionInsert, state: EditorState) => {
			const body = snippet.body;
			if (!body) {
				return false;
			}

			if (Array.isArray(body)) {
				const fragment = Fragment.fromJSON(state.schema, body);
				return insert(fragment);
			}

			return insert(body);
		},
	};
};
